import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { AgentAssistantRequestParamsSchema, AgentAssistantRequestParams, type FileSelectionTarget } from './schemas'
import { getStandardFederalLRRSources } from '@/jurisdiction-mappings/jurisdiction-maps'

export type AgentConversationFormData = {
  requestParams: AgentAssistantRequestParams
  queryIsSubmitting: boolean
}

/** An upload that is in flight: tracked by its client id, and which slot it will land in, until the server id arrives. */
export type PendingUpload = { clientTmpId: string; target: FileSelectionTarget }

export type AgentConversationFormState = {
  forms: Partial<{ [conversationId: string]: AgentConversationFormData }>
  pendingUploadsByConversationId: Partial<Record<string, PendingUpload[]>>
  attachedPendingUploadsByConversationId: Partial<Record<string, PendingUpload[]>>
  // Per-conversation flag driving the files-reset notice
  showFilesResetNoticeByConversationId: Partial<Record<string, boolean>>
  // Global flag to indicate if uploaded/selected files should be treated as templates
  isTemplateMode: boolean
  // Flag to open the sources dialog when the next chat mounts (e.g. redirecting from legacy research)
  openSourcesDialogOnMount: boolean
}

const initialState: AgentConversationFormState = {
  forms: {},
  pendingUploadsByConversationId: {},
  attachedPendingUploadsByConversationId: {},
  showFilesResetNoticeByConversationId: {},
  isTemplateMode: false,
  openSourcesDialogOnMount: false,
}

// Create a new form with default values
const createNewForm = (): AgentConversationFormData => ({
  requestParams: AgentAssistantRequestParamsSchema.parse({}), // Use schema to apply defaults
  queryIsSubmitting: false,
})

const setUserFilesRag = (requestParams: AgentAssistantRequestParams, userFileIds: string[]) => {
  const dedupedIds = Array.from(new Set(userFileIds))
  requestParams.user_files_rag = { user_file_ids: dedupedIds }
}

const setWritingSampleFiles = (requestParams: AgentAssistantRequestParams, userFileIds: string[]) => {
  const dedupedIds = Array.from(new Set(userFileIds))
  requestParams.writing_sample_files = { user_file_ids: dedupedIds }
}

// Helper to clear the files-reset notice flag
const clearFilesResetNotice = (state: AgentConversationFormState, conversationId: string) => {
  state.showFilesResetNoticeByConversationId[conversationId] = false
}

// Write back a per-conversation upload list, dropping the conversation entry once it empties.
const setPendingUploads = (map: Partial<Record<string, PendingUpload[]>>, conversationId: string, uploads: PendingUpload[]) => {
  if (uploads.length === 0) {
    delete map[conversationId]
  } else {
    map[conversationId] = uploads
  }
}

// Add a pending upload to a per-conversation map, de-duped by client id.
const addUploadToMap = (map: Partial<Record<string, PendingUpload[]>>, conversationId: string, upload: PendingUpload) => {
  const uploads = map[conversationId] ?? []
  if (uploads.some((existing) => existing.clientTmpId === upload.clientTmpId)) return
  setPendingUploads(map, conversationId, [...uploads, upload])
}

// Remove a pending upload from a per-conversation map by client id.
const removeUploadFromMap = (map: Partial<Record<string, PendingUpload[]>>, conversationId: string, clientTmpId: string) => {
  setPendingUploads(
    map,
    conversationId,
    (map[conversationId] ?? []).filter((upload) => upload.clientTmpId !== clientTmpId)
  )
}

// Detach in-flight user-file uploads from the form (e.g. when the user switches the file source).
// Writing-sample uploads live in their own request field, so they are left attached and can still resolve.
const detachUserFilePendingUploads = (state: AgentConversationFormState, conversationId: string) => {
  setPendingUploads(
    state.attachedPendingUploadsByConversationId,
    conversationId,
    (state.attachedPendingUploadsByConversationId[conversationId] ?? []).filter((upload) => upload.target !== 'user-files')
  )
}

// Helper to validate the form or initialize it with default values
const validateOrInitializeForm = (state: AgentConversationFormState, conversationId: string): AgentConversationFormData => {
  const form = state.forms[conversationId]

  if (!form) {
    const newForm = createNewForm()
    state.forms[conversationId] = newForm
    return newForm
  }

  const validation = AgentAssistantRequestParamsSchema.safeParse(form.requestParams)

  if (!validation.success) {
    const newForm = createNewForm()
    newForm.queryIsSubmitting = form.queryIsSubmitting
    state.forms[conversationId] = newForm
    return newForm
  }

  return form
}

/**
 * Agent Conversations Form Slice
 */
export const agentConversationFormSlice = createSlice({
  name: 'agentConversationFormState',
  initialState,
  reducers: {
    // ============== Listener Middleware Triggers ============== >
    submitForm: (state, action: PayloadAction<{ conversationId: string }>) => {
      // Submitting is handled by listener middleware

      // Clear the files-reset notice since the outgoing query drops the legacy selections
      clearFilesResetNotice(state, action.payload.conversationId)
    },

    sendStop: (_state, _action: PayloadAction<{ conversationId: string }>) => {
      // Handled by listener middleware
    },

    // ============== Slice Actions ============== >
    nullifyData: () => initialState,

    // Create a new form
    createForm: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      state.forms[conversationId] = createNewForm()
    },

    // Set the input text
    setInputText: (state, action: PayloadAction<{ conversationId: string; text: string }>) => {
      const { conversationId, text } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      form.requestParams.input_text = text
    },

    // Set the query submitting state
    setQueryIsSubmitting: (state, action: PayloadAction<{ conversationId: string; isSubmitting: boolean }>) => {
      const { conversationId, isSubmitting } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      form.queryIsSubmitting = isSubmitting
    },

    // Set all selected user file ids.
    setUserFiles: (state, action: PayloadAction<{ conversationId: string; userFileIds: string[] }>) => {
      const { conversationId, userFileIds } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      setUserFilesRag(form.requestParams, userFileIds)
      form.requestParams.file_paths = []
      detachUserFilePendingUploads(state, conversationId)

      // Clear the files-reset notice since the user added new files
      clearFilesResetNotice(state, conversationId)
    },

    // Add a user file id to the form.
    addUserFile: (state, action: PayloadAction<{ conversationId: string; userFileId: string }>) => {
      const { conversationId, userFileId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      const existingIds = form.requestParams.user_files_rag?.user_file_ids ?? []
      setUserFilesRag(form.requestParams, [...existingIds, userFileId])
      form.requestParams.file_paths = []

      // Clear the files-reset notice since the user added a new file
      clearFilesResetNotice(state, conversationId)
    },

    // Remove a user file id from the form.
    removeUserFile: (state, action: PayloadAction<{ conversationId: string; userFileId: string }>) => {
      const { conversationId, userFileId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      const existingIds = form.requestParams.user_files_rag?.user_file_ids ?? []
      setUserFilesRag(
        form.requestParams,
        existingIds.filter((id) => id !== userFileId)
      )
    },

    // Clear all selected user file ids.
    clearUserFiles: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      form.requestParams.user_files_rag = { user_file_ids: [] }
      detachUserFilePendingUploads(state, conversationId)
    },

    addPendingUserFileUpload: (state, action: PayloadAction<{ conversationId: string; clientTmpId: string; target: FileSelectionTarget }>) => {
      const { conversationId, clientTmpId, target } = action.payload
      const form = validateOrInitializeForm(state, conversationId)

      // A regular upload enters user_files_rag mode; a writing-sample upload leaves the request modes
      // untouched — addWritingSampleFile enforces its exclusivity once the upload resolves.
      if (target === 'user-files') {
        form.requestParams.file_paths = []
        if (!form.requestParams.user_files_rag) {
          form.requestParams.user_files_rag = { user_file_ids: [] }
        }
      }

      // Clear the files-reset notice since the user uploaded a file
      clearFilesResetNotice(state, conversationId)

      addUploadToMap(state.pendingUploadsByConversationId, conversationId, { clientTmpId, target })
      addUploadToMap(state.attachedPendingUploadsByConversationId, conversationId, { clientTmpId, target })
    },

    removePendingUserFileUpload: (state, action: PayloadAction<{ conversationId: string; clientTmpId: string }>) => {
      const { conversationId, clientTmpId } = action.payload
      removeUploadFromMap(state.pendingUploadsByConversationId, conversationId, clientTmpId)
      removeUploadFromMap(state.attachedPendingUploadsByConversationId, conversationId, clientTmpId)
    },

    // Add a legacy file path to the form.
    addFile: (state, action: PayloadAction<{ conversationId: string; filePath: string }>) => {
      const { conversationId, filePath } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      // Keep request modes separate per submit: legacy file_paths mode disables user_files_rag.
      form.requestParams.user_files_rag = null
      detachUserFilePendingUploads(state, conversationId)
      if (!form.requestParams.file_paths.includes(filePath)) {
        form.requestParams.file_paths.push(filePath)
      }
    },

    // Add a list of files to the form
    addFiles: (state, action: PayloadAction<{ conversationId: string; filePaths: string[]; templatePaths: string[] }>) => {
      const { conversationId, filePaths, templatePaths } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      form.requestParams.user_files_rag = null
      detachUserFilePendingUploads(state, conversationId)
      form.requestParams.file_paths = Array.from(new Set([...form.requestParams.file_paths, ...filePaths]))
      form.requestParams.template_paths = Array.from(new Set([...form.requestParams.template_paths, ...templatePaths]))
    },

    // Remove a file from the form
    removeFile: (state, action: PayloadAction<{ conversationId: string; filePath: string }>) => {
      const { conversationId, filePath } = action.payload
      const form = validateOrInitializeForm(state, conversationId)

      // Remove from file_paths
      form.requestParams.file_paths = form.requestParams.file_paths.filter((file) => file !== filePath)
    },

    // Clear files from the form
    clearFiles: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      form.requestParams.file_paths = []
    },

    clearLegacyFilesForNewFilesCutover: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      form.requestParams.file_paths = []
      form.requestParams.template_paths = []
      form.requestParams.user_files_rag = { user_file_ids: [] }
      detachUserFilePendingUploads(state, conversationId)

      // We just reset this conversation's legacy selections, so surface the files-reset notice.
      state.showFilesResetNoticeByConversationId[conversationId] = true
    },

    // Add a file as a template
    addTemplateFile: (state, action: PayloadAction<{ conversationId: string; filePath: string }>) => {
      const { conversationId, filePath } = action.payload
      const form = validateOrInitializeForm(state, conversationId)

      // Add to template_paths
      form.requestParams.template_paths = [filePath] // Restrict to 1 template
    },

    // Remove a template file
    removeTemplateFile: (state, action: PayloadAction<{ conversationId: string; filePath: string }>) => {
      const { conversationId, filePath } = action.payload
      const form = validateOrInitializeForm(state, conversationId)

      // Remove from template_paths
      form.requestParams.template_paths = form.requestParams.template_paths.filter((file) => file !== filePath)
    },

    // Clear template files from the form
    clearTemplateFiles: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      form.requestParams.template_paths = []
    },

    // Add a writing sample by user_file id (new files UX). Mirrors addTemplateFile.
    addWritingSampleFile: (state, action: PayloadAction<{ conversationId: string; userFileId: string }>) => {
      const { conversationId, userFileId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)

      // Restrict to 1 writing sample, and clear legacy template_paths
      // (the two are mutually exclusive on the backend).
      setWritingSampleFiles(form.requestParams, [userFileId])
      form.requestParams.template_paths = []

      clearFilesResetNotice(state, conversationId)
    },

    // Remove a writing sample by user_file id (new files UX).
    removeWritingSampleFile: (state, action: PayloadAction<{ conversationId: string; userFileId: string }>) => {
      const { conversationId, userFileId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      const existingIds = form.requestParams.writing_sample_files?.user_file_ids ?? []
      setWritingSampleFiles(
        form.requestParams,
        existingIds.filter((id) => id !== userFileId)
      )
    },

    // Clear writing samples from the form (new files UX).
    clearWritingSampleFiles: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      form.requestParams.writing_sample_files = { user_file_ids: [] }
    },

    // Set template mode flag
    setTemplateMode: (state, action: PayloadAction<boolean>) => {
      state.isTemplateMode = action.payload
    },

    // Set flag to open sources dialog on next chat mount
    setOpenSourcesDialogOnMount: (state, action: PayloadAction<boolean>) => {
      state.openSourcesDialogOnMount = action.payload
    },

    // Set include unpublished cases
    setIncludeUnpublishedCases: (state, action: PayloadAction<{ conversationId: string; includeUnpublishedCases: boolean }>) => {
      const { conversationId, includeUnpublishedCases } = action.payload

      const form = validateOrInitializeForm(state, conversationId)
      if (form.requestParams.caselaw_search_filter) {
        form.requestParams.caselaw_search_filter.include_unpublished_cases = includeUnpublishedCases
      } else {
        form.requestParams.caselaw_search_filter = {
          jurisdiction_codes: [],
          include_unpublished_cases: includeUnpublishedCases,
        }
      }
    },

    // Clear research jurisdiction codes from request params (keeps UI filters intact for later restore)
    clearResearchJurisdictions: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)

      if (form.requestParams.caselaw_search_filter) {
        form.requestParams.caselaw_search_filter.jurisdiction_codes = []
      }
      if (form.requestParams.lrr_search_filter) {
        form.requestParams.lrr_search_filter.jurisdiction_codes = []
      }
    },

    // Set caselaw date range
    setCaselawDateRange: (state, action: PayloadAction<{ conversationId: string; dateLow: string | null; dateHigh: string | null }>) => {
      const { conversationId, dateLow, dateHigh } = action.payload

      const form = validateOrInitializeForm(state, conversationId)
      if (form.requestParams.caselaw_search_filter) {
        form.requestParams.caselaw_search_filter.date_low = dateLow
        form.requestParams.caselaw_search_filter.date_high = dateHigh
      } else {
        form.requestParams.caselaw_search_filter = {
          jurisdiction_codes: [],
          date_low: dateLow,
          date_high: dateHigh,
          include_unpublished_cases: false,
        }
      }
    },

    // Restore request params from backend (used when loading conversation data)
    restoreRequestParams: (state, action: PayloadAction<{ conversationId: string; requestParams: AgentAssistantRequestParams }>) => {
      const { conversationId, requestParams } = action.payload
      const form = validateOrInitializeForm(state, conversationId)

      //Convert legacy year_low/year_high to date_low/date_high if present
      const caselawSearchFilter = requestParams.caselaw_search_filter
      const hasLegacyYearFilter = caselawSearchFilter && (caselawSearchFilter.year_low || caselawSearchFilter.year_high)

      const transformedCaselawFilter = hasLegacyYearFilter
        ? {
            ...caselawSearchFilter,
            date_low: typeof caselawSearchFilter.year_low === 'number' ? new Date(Date.UTC(caselawSearchFilter.year_low, 0, 1)).toISOString() : null,
            date_high:
              typeof caselawSearchFilter.year_high === 'number'
                ? new Date(Date.UTC(caselawSearchFilter.year_high, 11, 31, 23, 59, 59, 999)).toISOString()
                : null,
            year_low: null,
            year_high: null,
          }
        : caselawSearchFilter

      form.requestParams = {
        ...form.requestParams,
        user_files_rag: requestParams.user_files_rag,
        file_paths: requestParams.file_paths,
        template_paths: requestParams.template_paths,
        writing_sample_files: requestParams.writing_sample_files,
        caselaw_search_filter: transformedCaselawFilter,
        lrr_search_filter: requestParams.lrr_search_filter,
      }
    },

    // Sync research filters to request params
    // This action reads from researchFiltersState and updates the requestParams
    // It should be dispatched after any change to the research filters UI state
    syncResearchFiltersToRequestParams: (
      state,
      action: PayloadAction<{
        conversationId: string
        stateLrrSources: string[]
        stateCaselawSources: string[]
        federalLrrSources: string[]
        federalCaselawSources: string[]
      }>
    ) => {
      const { conversationId, stateLrrSources, stateCaselawSources, federalLrrSources, federalCaselawSources } = action.payload
      const form = validateOrInitializeForm(state, conversationId)

      // Check if any federal content is selected
      const hasFederalContent = federalLrrSources.length > 0 || federalCaselawSources.length > 0

      // If federal content is selected, automatically include standard federal LRR sources
      // These sources don't appear in the UI, only in the payload
      const federalLrrToInclude = hasFederalContent ? Array.from(new Set([...federalLrrSources, ...getStandardFederalLRRSources()])) : federalLrrSources

      // Combine state and federal sources
      const allLrrSources = Array.from(new Set([...stateLrrSources, ...federalLrrToInclude]))
      const allCaselawSources = Array.from(new Set([...stateCaselawSources, ...federalCaselawSources]))

      // Update LRR search filter
      form.requestParams.lrr_search_filter = {
        jurisdiction_codes: allLrrSources,
      }

      // Update caselaw search filter (preserve other properties)
      const existingCaselawFilter = form.requestParams.caselaw_search_filter || {
        jurisdiction_codes: [],
        include_unpublished_cases: false,
      }
      form.requestParams.caselaw_search_filter = {
        ...existingCaselawFilter,
        jurisdiction_codes: allCaselawSources,
      }
    },
  },
})

// Actions
export const AgentConversationFormActions = agentConversationFormSlice.actions

export default agentConversationFormSlice.reducer
